/* One dialog, used by every screen that writes something.
 *
 * <dialog> rather than a div with a z-index: the browser already owns the top layer,
 * the backdrop, Escape, and returning focus to whatever opened it. The shipped demo
 * hand-rolled all four in openModal() and got Escape right and focus-return wrong.
 *
 * A dialog NEVER closes itself on save. The caller closes it, after the write
 * resolved, because a form that vanishes while the write is still in flight is a form
 * that has lied about what happened.
 */
import { useEffect, useRef, type ReactNode } from 'react';

export function Modal({ title, onClose, children, footer }: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);

  return (
    <dialog
      ref={ref}
      className="modal"
      onCancel={(e) => { e.preventDefault(); onClose(); }}
      /* Clicking the backdrop is a click on the dialog element itself: anything
         inside it is one of the children. */
      onClick={(e) => { if (e.target === ref.current) onClose(); }}
    >
      <form method="dialog" onSubmit={(e) => e.preventDefault()}>
        <header>
          <h2 className="t-heading-h3">{title}</h2>
          <span className="spacer" />
          <button type="button" className="linkish t-label-small" onClick={onClose}>Close</button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer>{footer}</footer>}
      </form>
    </dialog>
  );
}

/** A labelled input. The label is always present: a placeholder is not a label. */
export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="formfield">
      <span className="t-label-small">{label}</span>
      {children}
      {hint && <span className="t-caption muted">{hint}</span>}
    </label>
  );
}
